import React from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { useLocationContext } from '../hooks/useLocationContext';

/**
 * Build URLs that honour the current org/location scope.
 *
 * The app has three URL "shapes" for the same feature, depending on who
 * the caller is and where they've scoped to:
 *
 *   flat                          /students          (LOCATION_ADMIN with a single membership)
 *   /loc/:locId/...               /loc/abc/students  (org-admin scoped into a loc)
 *   /org/:orgId/loc/:locId/...    /org/o/loc/l/students  (super-admin scoped into org+loc)
 *
 * `useLocationContext().urlPrefix` returns the right prefix for the
 * current URL — '' for flat, '/loc/abc' for org-admin, '/org/o/loc/l' for
 * super-admin. Concatenating that with a feature path gives a scoped URL.
 *
 * A link built without the prefix silently sends the user to the flat
 * URL, which LocationContext sees as "no scope" and the layout reverts
 * to platform-level. Always build scoped URLs through these helpers
 * rather than concatenating `urlPrefix` inline.
 */

/** Hook form. Returns a function that prepends the current scope prefix
 *  to a path. Always returns a string starting with '/'. */
export function useScopedPath(): (path: string) => string {
  const { urlPrefix } = useLocationContext();
  return React.useCallback(
    (path: string) => {
      const normalised = path.startsWith('/') ? path : `/${path}`;
      return `${urlPrefix}${normalised}`;
    },
    [urlPrefix]
  );
}

export interface ScopedLinkProps extends Omit<LinkProps, 'to'> {
  /** Path relative to the current scope. Will be prefixed automatically.
   *  Pass an absolute path that already includes a scope (e.g. '/login')
   *  by using react-router's <Link> directly instead. */
  to: string;
}

/**
 * Drop-in replacement for react-router's <Link> that automatically
 * applies the current org/location URL prefix. Use this for any link
 * that should preserve the user's scope context — i.e. every in-app
 * link to a feature like /students, /forms, /fire-drills.
 *
 * Don't use for external links, /login, /logout, /parent/*, or anywhere
 * else the scope prefix doesn't apply.
 */
export const ScopedLink: React.FC<ScopedLinkProps> = ({ to, ...rest }) => {
  const scoped = useScopedPath();
  return <Link to={scoped(to)} {...rest} />;
};
